import {Injectable} from '@angular/core';
import {Course} from '../models/course.model';
import {Observable, Subject} from 'rxjs';
import {HttpRequestsConfigService} from './http-requests-config.service';

@Injectable({
  providedIn: 'root'
})
export class CourceService {

  courses: Course[] = [];
  courseSubject$: Subject<Course[]> = new Subject<Course[]>();

  constructor(
    private readonly httpRequestConfig: HttpRequestsConfigService) {
  }

  emitCourseSubject() {
    this.courseSubject$.next(this.courses.slice());
  }


  // Method for admin - shows all courses without any filtering
  public getAllCourses(): void {
    this.httpRequestConfig.getAll<Course>('/courses')
      .subscribe(
        (response: Course[]) => {
          let coursesData = response;
          if (response && (response as any).courses) {
            coursesData = (response as any).courses;
          }

          // Show all courses without any date filtering for admin
          this.courses = coursesData;
          this.emitCourseSubject();
        }
      );
  }

  // Method for archive - shows archived courses (past courses) or courses for a specific year
  public getArchivedCourses(year?: string): void {
    this.httpRequestConfig.getAll<Course>('/courses')
      .subscribe(
        (response: Course[]) => {
          let coursesData = response;
          if (response && (response as any).courses) {
            coursesData = (response as any).courses;
          }

          this.courses = coursesData.filter((course: Course) => {
            if (!year) {
              // If no year is specified, show archived courses only
              // A course is considered archived if its end date has already passed (before today)
              const currentDate = new Date();

              // Set current date to start of day to ensure we're comparing properly
              currentDate.setHours(0, 0, 0, 0);

              if (course.end_date) {
                const endDate = new Date(course.end_date);

                // Set end date to start of day for consistent comparison
                const endDateTime = new Date(endDate);
                endDateTime.setHours(0, 0, 0, 0);

                // Only include courses where the end date is BEFORE today (not including today)
                // This means courses ending TODAY remain visible in the main program until tomorrow
                return endDateTime < currentDate;
              }
              return false; // If no end date, don't include in archive
            }

            // If year is specified, filter by that year
            if (course.end_date) {
              const courseEndYear = new Date(course.end_date).getFullYear();
              return courseEndYear === parseInt(year, 10);
            }
            return false; // If no end date, don't include in specific year
          });
          this.emitCourseSubject();
        }
      );
  }

  // Method for program - shows current/upcoming courses only
  public getCurrentCourses(): void {
    this.httpRequestConfig.getAll<Course>('/courses')
      .subscribe(
        (response: Course[]) => {
          let coursesData = response;
          if (response && (response as any).courses) {
            coursesData = (response as any).courses;
          }

          this.courses = coursesData.filter((course: Course) => {
            // Show current/upcoming courses only
            // A course is considered current if its end date is after today (meaning it ends today or in the future)
            const currentDate = new Date();

            // Set current date to start of day to ensure we're comparing properly
            currentDate.setHours(0, 0, 0, 0);

            if (course.end_date) {
              const endDate = new Date(course.end_date);

              // Set end date to start of day for consistent comparison
              const endDateTime = new Date(endDate);
              endDateTime.setHours(0, 0, 0, 0);

              // Include courses where the end date is today OR in the future
              // This means courses ending TODAY will still be visible
              return endDateTime >= currentDate;
            }
            return false; // If no end date, don't include
          });
          this.emitCourseSubject();
        }
      );
  }

  public getCourses(year?: string): Observable<Course[]> {
    return this.httpRequestConfig.getAll<Course>('/courses')
  }

  getCourse(course_id: number) {
    return this.httpRequestConfig.get<any>('/courses', course_id);
  }

  createCourse(course: Course): Observable<Course> {
    return this.httpRequestConfig.post<any>('/courses/add', course);
  }

  editCourse(course_id: number, course: Course): Observable<Course> {
    return this.httpRequestConfig.put<any>(
      '/courses/update', course_id, course
    );
  }

  deleteCourse(course_id: number) {
    return this.httpRequestConfig.delete<any>('/courses/delete', course_id);
  }

}
